<template>
  <div class="adminUsers">
    <section class="wrapper">
      <section class="headlineClass">
        <h1>
          {{ 'adminUsersTranslation.manageUsers' | translate }}
        </h1>
      </section>
      <button
        type="button"
        v-if="!loading && selectedView === 0"
        id="buttonCreateAccount"
        class="createButton"
        @click="showCreateAccountModal"
      >
        {{ 'adminUsersTranslation.createAccount' | translate }}
      </button>
    </section>

    <BaseTabs
      v-if="$enabledFeatures.includes('deletion-requests')"
      :tabDetails="tabDetails"
      :selectedView="selectedView"
      @show-view="showView"
    ></BaseTabs>

    <section class="smallerComp">
      <section v-show="!loading && selectedView === 0" id="sectionUsers">
        <BaseSearch
          id="searchUsers"
          type="admin-users"
          @get-new-list="getNewList"
          @reset-page="resetPage"
        ></BaseSearch>

        <BasePagination
          id="paginationUsersTop"
          :numberOfPages="userListData.allPages"
          :currentPage="userListData.currentPage"
          @new-page="getNextPage"
        ></BasePagination>

        <table v-if="isNonEmptyArray(userListData.list)" id="userList" class="permissionsTable">
          <tr
            v-for="(user, userIndex) in userListData.list"
            :id="'entryUser' + user.id"
            :key="user.id"
            class="wrapper interventionElement"
          >
            <div class="tableFirstColumn">
              <label>
                {{ user.firstname }} {{ user.lastname }} ({{ user.name }})
              </label>
              {{ user.email }}
            </div>
            <template v-if="user.verified">
              <div v-for="role in rolesArray" :key="role" class="tableCell notBold">
                <label :class="{ notSelectedRole: !user.role.includes(role) }">
                  {{ 'adminUsersTranslation.' + role | translate }}
                  <i
                    class="fas"
                    style="width: 15px;"
                    :class="[user.role.includes(role) ? 'fa-check' : 'fa-times']"
                    :id="'role' + role + user.id"
                  ></i>
                </label>
              </div>
            </template>
            <div v-else class="resendButtonSec">
              <button
                type="button"
                :id="'buttonResendVerification' + user.id"
                :disabled="isUpdated"
                @click="resendVerification(user.email)"
              >
                {{ 'adminUsersTranslation.resendVerification' | translate }}
              </button>
            </div>
            <div class="adminButtons">
              <i
                class="fas fa-pen fa-lg"
                :id="'buttonEditUser' + user.id"
                v-tooltip="$t('adminUsersTranslation.edit')"
                :disabled="isUpdated"
                :style="{ visibility: showIfVerified(user.verified) }"
                @click="changeRolesOfUser(userIndex, user)"
              ></i>

              <i
                class="fa fa-trash fa-lg"
                :id="'buttonDeleteUser' + user.id"
                v-tooltip="$t('generalTranslation.delete')"
                :disabled="isUpdated"
                :style="{ visibility: showIfOtherUser(user.id) }"
                @click="showDeleteUser(user)"
              ></i>
            </div>
          </tr>
        </table>

        <section v-else id="noUsers">
          {{ 'permissionsTranslation.noUsers' | translate }}
        </section>

        <BasePagination
          id="paginationUsersBottom"
          :numberOfPages="userListData.allPages"
          :currentPage="userListData.currentPage"
          class="paginationBottom"
          @new-page="getNextPage"
        ></BasePagination>
      </section>

      <section v-show="!loading && selectedView === 1" id="sectionDeletion">
        <!--
        <BaseSearch
          id="searchDeletions"
          type="admin-users-deletion"
          @get-new-list="getNewListDeletion"
          @reset-page="resetPageDeletion"
        ></BaseSearch> -->

        <BasePagination
          id="paginationDeletionsTop"
          :numberOfPages="requestListData.allPages"
          :currentPage="requestListData.currentPage"
          @new-page="getNextPageDeletion"
        ></BasePagination>

        <table v-if="isNonEmptyArray(requestListData.list)" id="deletionList" class="deleteReqTable">
          <tr>
            <th>
              {{ 'adminUsersTranslation.userDetails' | translate }}
            </th>
            <th>
              {{ 'adminUsersTranslation.studiesOfUser' | translate }}
            </th>
            <th>
              {{ 'adminUsersTranslation.eCoachManager' | translate }}
            </th>
            <th>
              {{ 'adminUsersTranslation.data' | translate }}
            </th>
            <th>
              {{ 'adminUsersTranslation.acceptDecline' | translate }}
            </th>
          </tr>
          <tr
            v-for="request in requestListData.list"
            :id="'entryDeletion' + request.id"
            :key="request.id"
            class="interventionElement"
          >
            <td>
              <div v-if="request.relationships.users?.data">
                <label>
                  {{ request.relationships.users.data.attributes.email }}
                </label>
                {{ getUserName(request.relationships.users.data.attributes) }}
              </div>
            </td>
            <td>
              <ul v-if="isNonEmptyArray(request.relationships.studies?.data)">
                <li v-for="study in request.relationships.studies.data" :key="study.id">
                  {{ study.attributes.name }}
                </li>
              </ul>
              <div v-else>
                {{ 'adminUsersTranslation.userHasNoStudies' | translate }}
              </div>
            </td>
            <td>
              <ul v-if="isNonEmptyArray(request.attributes.studies)">
                <li v-for="(study, index) in request.attributes.studies" :key="index">
                  {{ getECoachManagers(study.ecoach_managers) }}
                </li>
              </ul>
              <div v-else>
                {{ 'generalTranslation.none' | translate }}
              </div>
            </td>
            <td>
              {{ getDataText(request.attributes.pseudonymized) }}
            </td>
            <td>
              <div class="adminButtons">
                <i
                  class="fas fa-check fa-lg"
                  :id="'buttonAcceptDeletion' + request.id"
                  v-tooltip="$t('adminUsersTranslation.acceptDeletion')"
                  :disabled="isUpdated"
                  @click="showAccountDeletion(request, true)"
                ></i>
                <i
                  class="fa fa-times fa-lg"
                  :id="'buttonDeclineDeletion' + request.id"
                  v-tooltip="$t('adminUsersTranslation.declineDeletion')"
                  :disabled="isUpdated"
                  @click="showAccountDeletion(request, false)"
                ></i>
              </div>
            </td>
          </tr>
        </table>

        <section v-else id="noDeletions">
          {{ 'adminUsersTranslation.noDeletionRequests' | translate }}
        </section>

        <BasePagination
          id="paginationDeletionsBottom"
          :numberOfPages="requestListData.allPages"
          :currentPage="requestListData.currentPage"
          class="paginationBottom"
          @new-page="getNextPageDeletion"
        ></BasePagination>
      </section>

      <i class="fa fa-spinner fa-spin fa-2x loadData" v-if="loading"></i>
    </section>

    <AdminUserCreate
      v-if="createAccountVisible"
      @close-modal="closeModalCreate"
    ></AdminUserCreate>

    <BaseModal
      v-if="updateRolesVisible"
      id="editUserModal"
      :headerText="$t('adminUsersTranslation.changePatientRoles', { email: emailOfSelectedUser })"
      :leftButtonText="$t('generalTranslation.save')"
      :disableLeftButton="invalidUserUpdate(selectedUser)"
      @close-modal="closeModalRoles"
    >
      <template v-slot:body>
        <h4>
          {{ nameOfSelectedUser }}
        </h4>

        <section v-for="role in rolesArray" :key="role" class="checkboxSection notBold">
          <label
            class="container"
            :class="{ notSelectedRole: role === 'admin' && Number(idOfUpdateUser) === userId }"
          >
            <i class="fas fa-check" v-if="role === 'admin' && Number(idOfUpdateUser) === userId"></i>
            {{ 'adminUsersTranslation.' + role | translate }}
            <input
              type="checkbox"
              v-if="role !== 'admin' || Number(idOfUpdateUser) !== userId"
              v-model="selectedUser.roles"
              :value="role" :id="'inputEditUserRole' + role"
            />
            <span class="checkmark" v-if="role !== 'admin' || Number(idOfUpdateUser) !== userId"></span>
          </label>
        </section>

        <section class="wrapper">
          <label>
            {{ 'adminUsersTranslation.email' | translate }} *
          </label>
          <input
            class="block"
            type="text"
            v-model="selectedUser.email"
            id="inputPatientEmail"
            :placeholder="$t('adminUsersTranslation.email')"
          />
          <label>
            {{ 'adminUsersTranslation.username' | translate }} {{ selectedUser.roles.length > 0 ? '*' : '' }}
          </label>
          <input
            class="block"
            type="text"
            v-model="selectedUser.name"
            id="inputPatientName"
            :placeholder="$t('adminUsersTranslation.username')"
          />
          <label>
            {{ 'adminUsersTranslation.firstname' | translate }} *
          </label>
          <input
            :disabled="selectedUser.roles.length === 0"
            class="block" type="text"
            v-model="selectedUser.firstname"
            id="inputPatientFirstname"
            :placeholder="$t('adminUsersTranslation.firstname')"
          />
          <label>
            {{ 'adminUsersTranslation.lastname' | translate }} *
          </label>
          <input
            :disabled="selectedUser.roles.length === 0"
            class="block"
            type="text"
            v-model="selectedUser.lastname"
            id="inputPatientLastname"
            :placeholder="$t('adminUsersTranslation.lastname')"
          />

          <div v-if="isInvalidEmail(selectedUser.email)" class="textMiddle errorMessage">
            {{ $t('loginTranslation.errorEnterValidEmail') }}
          </div>
          <div v-else-if="invalidRoleChange(selectedUser)" class="textMiddle errorMessage">
            {{ $t('adminUsersTranslation.errorIncompleteForm') }}
          </div>
          <div v-else-if="selectedUser.roles.length === 0 && (selectedUser.firstname || selectedUser.lastname)" class="textMiddle textTip">
            {{ $t('adminUsersTranslation.infoDeletedUserNames') }}
          </div>
          <div v-else-if="!changedUser(selectedUser)" class="textMiddle textInfo">
            {{ $t('adminUsersTranslation.infoNoChangesMade') }}
          </div>
        </section>
      </template>
    </BaseModal>

    <BaseModal
      v-if="deleteUserVisible"
      id="deleteUserModal"
      :headerText="$t('adminUsersTranslation.deleteWarning', { email: selectedUser.email })"
      :leftButtonText="$t('generalTranslation.delete')"
      :disableButtons="isUpdated"
      @close-modal="closeModalDeleteUser"
    >
      <template v-slot:body>
        <div>
          <input type="radio" id="yes" :value="true" v-model="pseudonymize">
          <span for="yes">
            {{ $t("myStudiesTranslation.pseudonymize") }}
          </span>
          <div style="margin-top: 5px;"></div>
          <input type="radio" id="no" :value="false" v-model="pseudonymize">
          <span for="no">
            {{ $t("myStudiesTranslation.hardDelete") }}
          </span>
        </div>
        <div style="margin-top: 20px;">
          <input
            class="checkmark"
            v-model="forceDelete"
            type="checkbox"
            id="forceDelete"
          >
          <span v-if="pseudonymize">
            {{ $t("myStudiesTranslation.forcePseudonymize") }}
          </span>
          <span v-else>
            {{ $t("myStudiesTranslation.forceDelete") }}
          </span>
          <i
            class="far fa-question-circle"
            v-tooltip="forceDeleteTooltip"
          ></i>
        </div>
        <div class="deletionRequestText">
          <strong><u>{{ $t("myStudiesTranslation.warning") }}</u></strong>
        </div>
      </template>
    </BaseModal>

    <BaseModal
      v-if="accountDeletionVisible"
      id="accountDeletionModal"
      :headerText="$t(
        'adminUsersTranslation.' + (selectedRequest.accept ? 'deleteWarning' : 'declineWarning'),
        { email: selectedRequest.email }
      )"
      :leftButtonText="accountDeletionButtonText"
      :disableButtons="isUpdated"
      @close-modal="closeModalDeleteAccount"
    >
      <template v-slot:body>
        <div v-if="selectedRequest.accept">
          <input
            class="checkmark"
            v-model="forceDeleteRequest"
            type="checkbox"
            id="forceDeleteRequest"
          >
          <span v-if="selectedRequest.pseudonymize">
            {{ $t("myStudiesTranslation.forcePseudonymize") }}
          </span>
          <span v-else>
            {{ $t("myStudiesTranslation.forceDelete") }}
          </span>
          <i
            class="far fa-question-circle"
            v-tooltip="forceDeleteTooltip"
          ></i>
        </div>
        <div :class="{ deletionRequestText: selectedRequest.accept }">
          <strong><u>{{ accountDeletionBodyText }}</u></strong>
        </div>
      </template>
    </BaseModal>

    <BaseNotification ref="notificationRef"></BaseNotification>
  </div>
</template>

<script>
import Vue from "vue";
import { mapGetters, mapMutations } from "vuex";
import httpHelper from "../mixins/httpHelper";
import utils from "../mixins/utils";
import AdminUserCreate from "./AdminUserCreate.vue";
import BaseTabs from "./BaseTabs.vue";
import BaseNotification from "./BaseNotification.vue";
import BaseModal from "./BaseModal.vue";
import BasePagination from "./BasePagination.vue";
import BaseSearch from "./BaseSearch.vue";

export default {
  name: "AdminUsers",

  components: {
    AdminUserCreate,
    BaseTabs,
    BaseNotification,
    BaseModal,
    BasePagination,
    BaseSearch,
  },

  mixins: [httpHelper, utils],

  data: function() {
    return{
      updateRolesVisible: false,
      loading: true,
      isUpdated: false,
      userListData: "",
      requestListData: "",
      filterTerm: "",
      deleteUserVisible: false,
      selectedRequest: "",
      selectedUser: null,
      createAccountVisible: false,
      selectedView: 0,
      accountDeletionVisible: false,
      rolesArray: ["admin", "ecoach", "editor"],
      pseudonymize: true,
      forceDelete: false,
      forceDeleteRequest: false,
    };
  },

  computed: {
    ...mapGetters([
      "getUserId",
      "getNotificationText",
    ]),

    userId: function() {
      return Number(this.getUserId);
    },

    idOfUpdateUser: function() {
      return this.selectedUser ? this.userListData.list[this.selectedUser.index].id : "";
    },

    emailOfSelectedUser: function() {
      if (this.selectedUser) {
        const user = this.userListData.list[this.selectedUser.index];
        if (user.email) {
          return user.email;
        }
      }
      return "";
    },

    nameOfSelectedUser: function() {
      let name = "";
      if (this.selectedUser) {
        const user = this.userListData.list[this.selectedUser.index];
        if (user.firstname) {
          name += user.firstname;
        }
        if (user.lastname) {
          name += (name !== "" ? " " : "") + user.lastname;
        }
        if (user.name) {
          name += (name !== "" ? " " : "") + `(${user.name})`;
        }
      }
      return name;
    },

    accountDeletionBodyText: function() {
      if (this.selectedRequest.accept) {
        const text = " " + Vue.i18n.translate("myStudiesTranslation.warning");
        if (this.selectedRequest.pseudonymize) {
          return Vue.i18n.translate("adminUsersTranslation.dataPseudonymized") + text;
        } else {
          return Vue.i18n.translate("adminUsersTranslation.dataDeleted") + text;
        }
      } else {
        return Vue.i18n.translate("adminUsersTranslation.declineText");
      }
    },

    accountDeletionButtonText: function() {
      return this.selectedRequest.accept
        ? Vue.i18n.translate("generalTranslation.delete")
        : Vue.i18n.translate("adminUsersTranslation.decline");
    },

    forceDeleteTooltip: function() {
      return {
        content: Vue.i18n.translate("myStudiesTranslation.forceDeleteTooltip"),
        theme: "info-tooltip",
      };
    },

    tabDetails: function() {
      return [
        { text: Vue.i18n.translate("adminUsersTranslation.allUsers"), show: true, id: "users" },
        { text: Vue.i18n.translate("adminUsersTranslation.deletionRequests"), show: true, id: "deletion" },
      ];
    },
  },

  mounted() {
    this.closeNotification();
    this.getAllUsersIncludingPermissions(1);
  },

  beforeRouteLeave(_to, _from, next) {
    if (this.createAccountVisible || this.updateRolesVisible) {
      next(false);
    } else {
      next();
    }
  },

  watch: {
    getNotificationText(newVal) {
      if (newVal != "") {
        if (newVal.type === "error") {
          this.loading = false;
          this.isUpdated = false;
        } else if (newVal.type === "success") {
          this.isUpdated = false;
        }
      }
    },
  },

  methods: {
    ...mapMutations([
      "SET_NOTIFICATIONTEXT",
    ]),

    changeRolesOfUser: function(index, user) {
      const roles = this.userListData.list[index].role;
      this.selectedUser = {
        index: index,
        roles: roles,
        isPatient: roles.length === 0,
        email: user.email,
        name: user.name,
        firstname: user.firstname,
        lastname: user.lastname,
      };
      this.updateRolesVisible = true;
    },

    invalidUserUpdate(user) {
      if (this.isInvalidEmail(user.email)) {
        return true;
      }
      if (user.isPatient) {
        if (user.roles.length === 0) {
          const oldUser = this.userListData.list[user.index];
          // If loaded patient had names defined, they should still be deletable even with no changes
          const unchangedPatient = this.textEquals(oldUser.email, user.email)
            && this.textEquals(oldUser.name, user.name)
            && this.stringEquals(oldUser.firstname, user.firstname) && !user.firstname
            && this.stringEquals(oldUser.lastname, user.lastname) && !user.lastname;
          return unchangedPatient || this.isEmptyText(user.email);
        } else {
          return this.incompleteUserForm(user);
        }
      } else {
        return this.invalidRoleChange(user) || !this.changedUser(user);
      }
    },

    invalidRoleChange: function(user) {
      return user.roles.length > 0 && this.incompleteUserForm(user);
    },

    incompleteUserForm: function(user) {
      return this.containsEmptyText(user.name, user.firstname, user.lastname);
    },

    changedUser(user) {
      const oldUser = this.userListData.list[user.index];
      const changedEmail = this.textNotEquals(oldUser.email, user.email);
      const changedNames = this.textNotEquals(oldUser.name, user.name)
        || this.stringNotEquals(oldUser.firstname, user.firstname)
        || this.stringNotEquals(oldUser.lastname, user.lastname);
      return changedEmail || changedNames || this.changedRoles(user.roles, oldUser.role);
    },

    changedRoles(roles1, roles2) {
      if (roles1 === roles2) { return false; }
      if (roles1.length !== roles2.length) { return true; }
      const rolesCount = new Map();
      for (const role of roles1) {
        const count = rolesCount.get(role);
        rolesCount.set(role, count === undefined ? 1 : count + 1);
      }
      for (const role of roles2) {
        const count = rolesCount.get(role);
        rolesCount.set(role, count === undefined ? -1 : count - 1);
      }
      for (const count of rolesCount.values()) {
        if (count !== 0) {
          return true;
        }
      }
      return false;
    },

    closeModalRoles: async function(done) {
      if (done) {
        if (this.selectedUser.isPatient) {
          this.isUpdated = true;
          this.SET_NOTIFICATIONTEXT({
            type: "load",
            text: Vue.i18n.translate("adminUsersTranslation.savePatientRolesLoad"),
          });
          await this.savePatientUser(done);
        } else if (this.changedUser(this.selectedUser)) {
          this.isUpdated = true;
          this.SET_NOTIFICATIONTEXT({
            type: "load",
            text: Vue.i18n.translate("adminUsersTranslation.savePatientRolesLoad"),
          });
          await this.saveChangedUser(done);
        }
      } else if (!this.isUpdated) {
        if (
          this.changedUser(this.selectedUser) &&
          (this.getNotificationText === "" ||
            this.getNotificationText.text !== Vue.i18n.translate("adminUsersTranslation.unsavedChangesRoles"))
        ) {
          this.SET_NOTIFICATIONTEXT({
            type: "error",
            text: Vue.i18n.translate("adminUsersTranslation.unsavedChangesRoles"),
          });
        } else {
          this.closeNotification();
          this.updateRolesVisible = false;
        }
      }
    },

    async savePatientUser(done) {
      const newRoles = this.selectedUser.roles;
      const oldUser = this.userListData.list[this.selectedUser.index];
      const changedEmail = this.textNotEquals(oldUser.email, this.selectedUser.email);
      const email = this.getChangedEmail(this.selectedUser.email, changedEmail);
      const changedName = this.textNotEquals(oldUser.name, this.selectedUser.name);
      const name = this.getChangedUsername(this.selectedUser.name, changedName);
      if (newRoles.length > 0) {
        const user = {
          email,
          name,
          firstname: this.selectedUser.firstname,
          lastname: this.selectedUser.lastname,
        };
        try {
          await this.updateUserProfileAsAdmin(this.idOfUpdateUser, user);
        } catch (error) {
          this.handleErrors(error, () => this.closeModalRoles(done), "addRoleToPatient");
          return;
        }
        try {
          await this.addRolesToUserRequest(this.idOfUpdateUser, newRoles);
          this.SET_NOTIFICATIONTEXT({
            type: "success",
            text: Vue.i18n.translate("adminUsersTranslation.savePatientRolesSuccess"),
          });
          this.getAllUsersIncludingPermissions(this.userListData.currentPage);
          this.updateRolesVisible = false;
        } catch (error) {
          this.handleErrors(error, () => this.closeModalRoles(done), "");
        }
      } else {
        const user = { email, name, firstname: null, lastname: null };
        try {
          await this.updateUserProfileAsAdmin(this.idOfUpdateUser, user);
          this.SET_NOTIFICATIONTEXT({
            type: "success",
            text: Vue.i18n.translate("adminUsersTranslation.savePatientRolesSuccess"),
          });
          this.getAllUsersIncludingPermissions(this.userListData.currentPage);
          this.updateRolesVisible = false;
        } catch (error) {
          this.handleErrors(error, () => this.closeModalRoles(done), "addRoleToPatient");
        }
      }
    },

    async saveChangedUser(done) {
      const oldUser = this.userListData.list[this.selectedUser.index];
      const oldRoles = oldUser.role;
      const newRoles = this.selectedUser.roles;
      const rolesToRemove = [];
      const rolesToAdd = [];
      for (const role of this.rolesArray) {
        if (oldRoles.includes(role) && !newRoles.includes(role)) {
          rolesToRemove.push(role);
        }
        if (!oldRoles.includes(role) && newRoles.includes(role)) {
          rolesToAdd.push(role);
        }
      }
      const changedEmail = this.textNotEquals(oldUser.email, this.selectedUser.email);
      const changedName = this.textNotEquals(oldUser.name, this.selectedUser.name);
      const changedNames = newRoles.length === 0
        || changedName
        || this.stringEquals(oldUser.firstname, this.selectedUser.firstname)
        || this.stringEquals(oldUser.lastname, this.selectedUser.lastname);
      if (changedEmail || changedNames) {
        const email = this.getChangedEmail(this.selectedUser.email, changedEmail);
        const name = this.getChangedUsername(this.selectedUser.name, changedName);
        const user = {
          email,
          name,
          firstname: newRoles.length === 0 ? null : this.selectedUser.firstname,
          lastname: newRoles.length === 0 ? null : this.selectedUser.lastname,
        };
        try {
          await this.updateUserProfileAsAdmin(this.idOfUpdateUser, user);
        } catch (error) {
          this.handleErrors(error, () => this.closeModalRoles(done), "addRoleToPatient");
          return;
        }
      }
      try {
        if (rolesToRemove.length !== 0) {
          await this.removeRolesFromUserRequest(this.idOfUpdateUser, rolesToRemove);
        }
        if (rolesToAdd.length !== 0) {
          await this.addRolesToUserRequest(this.idOfUpdateUser, rolesToAdd);
        }
        this.SET_NOTIFICATIONTEXT({
          type: "success",
          text: Vue.i18n.translate("adminUsersTranslation.savePatientRolesSuccess"),
        });
        this.getAllUsersIncludingPermissions(this.userListData.currentPage);
        this.updateRolesVisible = false;
      } catch (error) {
        this.handleErrors(error, () => this.closeModalRoles(done), "");
      }
    },

    getChangedUsername: function(name, changedName) {
      if (!name) {
        return null;
      }
      if (!changedName) {
        return undefined;
      }
      const trimmed = name.trim();
      return trimmed || null;
    },

    getChangedEmail: function(email, changedEmail) {
      if (!email || !changedEmail) {
        return undefined;
      }
      const trimmed = email.trim();
      return trimmed || undefined;
    },

    getRoleChangeHeader: function(user) {
      // const key = user.isPatient ? "adminUsersTranslation.changePatientRoles" : "adminUsersTranslation.changeRoles";
      const key = "adminUsersTranslation.changePatientRoles";
      return Vue.i18n.translate(key, { email: user.email });
    },

    resendVerification: function(userMail) {
      this.isUpdated = true;
      this.SET_NOTIFICATIONTEXT({
        type: "load",
        text: Vue.i18n.translate("adminUsersTranslation.resendVerificationLoad"),
      });
      this.resendVerificationForUserRequest(userMail);
    },

    getNewList: function(filterTerm) {
      this.filterTerm = filterTerm;
      this.getAllUsersIncludingPermissions(1);
    },

    getNewListDeletion: function(filterTerm) {
      this.filterTerm = filterTerm;
      this.getDeletionRequests(1);
    },

    resetPage: function() {
      this.userListData.currentPage = 1;
    },

    resetPageDeletion: function() {
      this.requestListData.currentPage = 1;
    },

    getNextPage(selectedNumber) {
      if (selectedNumber != "") {
        this.getAllUsersIncludingPermissions(selectedNumber);
      }
    },

    getNextPageDeletion(selectedNumber) {
      if (selectedNumber != "") {
        this.getDeletionRequests(selectedNumber);
      }
    },

    showView: function(view) {
      this.loading = true;
      this.selectedView = view;
      this.filterTerm = "";
      if (view === 0) {
        this.getAllUsersIncludingPermissions(1);
      } else {
        this.getDeletionRequests(1);
      }
    },

    getAllUsersIncludingPermissions: function(page) {
      this.loading = true;
      this.requestAllUsersIncludingPermissions(page, this.filterTerm)
        .then((response) => {
          this.userListData = response;
          this.loading = false;
        })
        .catch((error) => {
          this.handleErrors(error, () => this.getAllUsersIncludingPermissions(page), "");
        });
    },

    getDeletionRequests: function(page) {
      this.loading = true;
      this.requestAccountDeletionRequests(page, this.filterTerm)
        .then((response) => {
          this.requestListData = response;
          this.loading = false;
        })
        .catch((error) => {
          this.handleErrors(error, () => this.getDeletionRequests(page), "");
        });
    },

    closeModalDeleteUser: function(done) {
      if (done) {
        this.isUpdated = true;
        this.deleteUserVisible = false;
        this.SET_NOTIFICATIONTEXT({
          type: "load",
          text: Vue.i18n.translate("adminUsersTranslation.deleteUserLoad"),
        });
        let requestPage = this.userListData.currentPage;
        if (this.userListData.list.length === 1) {
          //delete last element of page -> request page before that
          requestPage--;
        }
        this.manuallyDeleteUser(done, requestPage);
      } else {
        this.deleteUserVisible = false;
      }
    },

    manuallyDeleteUser: function(done, requestPage) {
      const request = this.pseudonymize
        ? this.pseudonymizeUser(this.selectedUser.id, this.forceDelete)
        : this.deleteUser(this.selectedUser.id, this.forceDelete);
      request.then(() => {
        this.getAllUsersIncludingPermissions(requestPage);
        this.SET_NOTIFICATIONTEXT({
          type: "success",
          text: Vue.i18n.translate("adminUsersTranslation.deleteUserSuccess"),
        });
        this.selectedUser = null;
      }).catch((error) => {
        this.handleErrors(error, () => this.closeModalDeleteUser(done), "manualDeletion");
      });
    },

    closeModalDeleteAccount: function(done) {
      this.accountDeletionVisible = false;
      if (done) {
        this.isUpdated = true;
        if (this.selectedRequest.accept) {
          this.acceptAccountDeletion();
        } else {
          this.declineAccountDeletion();
        }
      }
    },

    acceptAccountDeletion: function() {
      this.SET_NOTIFICATIONTEXT({
        type: "load",
        text: Vue.i18n.translate("adminUsersTranslation.acceptAccountDeletionLoad"),
      });
      let requestPage = this.requestListData.currentPage;
      if (this.requestListData.list.length === 1) {
        //delete last element of page -> request page before that
        requestPage--;
      }
      const request = this.selectedRequest.pseudonymize
        ? this.pseudonymizeUserRequest(this.selectedRequest.userId, this.forceDeleteRequest)
        : this.deleteUserRequest(this.selectedRequest.userId, this.forceDeleteRequest);
      request.then(() => {
        this.getDeletionRequests(requestPage);
        this.SET_NOTIFICATIONTEXT({
          type: "success",
          text: Vue.i18n.translate("adminUsersTranslation.acceptAccountDeletionSuccess"),
        });
        this.selectedRequest = "";
      })
      .catch((error) => {
        this.handleErrors(error, () => this.acceptAccountDeletion(), "manualDeletion");
      });
    },

    declineAccountDeletion: function() {
      this.SET_NOTIFICATIONTEXT({
        type: "load",
        text: Vue.i18n.translate("adminUsersTranslation.declineAccountDeletionLoad"),
      });
      let requestPage = this.requestListData.currentPage;
      if(this.requestListData.list.length === 1){
        //delete last element of page -> request page before that
        requestPage--;
      }
      this.declineAccountDeletionRequest(this.selectedRequest.id)
        .then(() => {
          this.getDeletionRequests(requestPage);
          this.SET_NOTIFICATIONTEXT({
            type: "success",
            text: Vue.i18n.translate("adminUsersTranslation.declineAccountDeletionSuccess"),
          });
          this.selectedRequest = "";
        })
        .catch((error) => {
          this.handleErrors(error, () => this.declineAccountDeletion(), "");
        });
    },

    closeNotification: function() {
      this.$refs["notificationRef"].closeNotification();
    },

    showCreateAccountModal: function() {
      this.closeNotification();
      this.createAccountVisible = true;
    },

    closeModalCreate: function(created) {
      this.createAccountVisible = false;
      if (created) {
        //get last page
        const reqPage = Math.ceil((this.userListData.total + 1) / this.$perPage);
        this.getAllUsersIncludingPermissions(reqPage);
      }
    },

    showDeleteUser: function(user) {
      this.closeNotification();
      this.selectedUser = user;
      this.pseudonymize = true;
      this.forceDelete = false;
      this.deleteUserVisible = true;
    },

    showAccountDeletion: function(request, accept) {
      this.closeNotification();
      this.selectedRequest = {
        accept: accept,
        id: request.id,
        userId: request.attributes.user_id,
        email: request.relationships.users?.data?.attributes.email || "",
        pseudonymize: request.attributes.pseudonymized,
      };
      this.forceDeleteRequest = false;
      this.accountDeletionVisible = true;
    },

    showIfVerified: function(verified) {
      return verified ? "visible" : "hidden";
    },

    showIfOtherUser: function(idOfUser) {
      return Number(idOfUser) === this.userId ? "hidden" : "visible";
    },

    getDataText: function(pseudonymized) {
      return Vue.i18n.translate("adminUsersTranslation." + (pseudonymized ? "pseudonymize" : "deleteAll"));
    },

    getUserName: function(user) {
      let name = user.firstname || "";
      if (user.lastname) {
        name += ((name !== "") ? " " : "") + user.lastname;
      }
      if (user.name) {
        name += ((name !== "") ? " " : "") + "(" + user.name + ")";
      }
      return name;
    },

    getECoachManagers: function(ecoachManagers) {
      if (this.isNonEmptyArray(ecoachManagers)) {
        return ecoachManagers.join(", ");
      } else {
        return Vue.i18n.translate("generalTranslation.none");
      }
    },

    isNonEmptyArray: function(array) {
      return Array.isArray(array) && array.length > 0;
    },
  }
}
</script>

<style scoped>
.deletionRequestText {
  margin-top: 20px;
  text-align: center;
}
</style>
